import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import InputField from '../components/InputField';
import Button from '../components/Button';
import { useI18n } from '../context/I18nContext';
import { canAttemptCheckout, detectBotSubmission, registerCheckoutAttempt } from '../security/botProtection';
import { isValidCardNumber, isValidExpiry, maskCardNumber, tokenizePaymentForDemo } from '../security/paymentSecurity';

const CheckoutPage = () => {
  const { items, cartTotal, clearCart } = useCart();
  const { t } = useI18n();
  const checkoutDisabled = true;
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [securityMessage, setSecurityMessage] = useState('');
  const [lastMaskedCard, setLastMaskedCard] = useState('');
  const formStartedAt = useRef(Date.now());

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address: '',
    city: '',
    zipCode: '',
    country: '',
    cardNumber: '',
    cardName: '',
    expiry: '',
    cvv: '',
    website: '',
  });

  const shipping = cartTotal >= 100 ? 0 : 9.99;
  const tax = cartTotal * 0.08;
  const total = cartTotal + shipping + tax;

  const validate = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = t('checkout.errors.firstNameRequired');
    if (!form.lastName.trim()) newErrors.lastName = t('checkout.errors.lastNameRequired');
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = t('checkout.errors.emailInvalid');
    if (!form.address.trim()) newErrors.address = t('checkout.errors.addressRequired');
    if (!form.city.trim()) newErrors.city = t('checkout.errors.cityRequired');
    if (!form.zipCode.trim()) newErrors.zipCode = t('checkout.errors.zipRequired');
    if (!form.country.trim()) newErrors.country = t('checkout.errors.countryRequired');
    if (!isValidCardNumber(form.cardNumber)) newErrors.cardNumber = t('checkout.errors.cardNumberInvalid');
    if (!form.cardName.trim()) newErrors.cardName = t('checkout.errors.cardNameRequired');
    if (!isValidExpiry(form.expiry)) newErrors.expiry = t('checkout.errors.expiryInvalid');
    if (!form.cvv.trim() || !/^\d{3,4}$/.test(form.cvv)) newErrors.cvv = t('checkout.errors.cvvInvalid');
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'cardNumber') {
      formatted = value.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
    }
    if (name === 'expiry') {
      formatted = value.replace(/\D/g, '').slice(0, 4);
      if (formatted.length >= 2) formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
    }
    if (name === 'cvv') {
      formatted = value.replace(/\D/g, '').slice(0, 4);
    }
    setForm(prev => ({ ...prev, [name]: formatted }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (securityMessage) setSecurityMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (checkoutDisabled) {
      setSecurityMessage(t('checkout.orderDisabled'));
      return;
    }

    const botCheck = detectBotSubmission({
      startedAt: formStartedAt.current,
      honeypot: form.website,
    });
    if (botCheck.blocked) {
      registerCheckoutAttempt(false);
      setSecurityMessage(t(`checkout.errors.${botCheck.reason}`));
      return;
    }

    const attemptWindow = canAttemptCheckout();
    if (!attemptWindow.allowed) {
      setSecurityMessage(t(`checkout.errors.${attemptWindow.reason}`));
      return;
    }

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      registerCheckoutAttempt(false);
      return;
    }

    setLoading(true);
    setSecurityMessage('');

    try {
      const paymentToken = await tokenizePaymentForDemo({
        cardNumber: form.cardNumber,
        cardName: form.cardName,
        expiry: form.expiry,
      });

      // Simulated secure payload: sensitive card fields are transformed then discarded.
      const securePayload = {
        customer: {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
        },
        amount: Number(total.toFixed(2)),
        currency: 'USD',
        cardLast4: form.cardNumber.replace(/\D/g, '').slice(-4),
        paymentToken,
      };

      if (!securePayload.paymentToken) throw new Error('tokenization_failed');

      setLastMaskedCard(maskCardNumber(form.cardNumber));

      setTimeout(() => {
        registerCheckoutAttempt(true);
        setForm(prev => ({
          ...prev,
          cardNumber: '',
          cardName: '',
          expiry: '',
          cvv: '',
          website: '',
        }));
        setErrors({});
        setSecurityMessage('');
        setLoading(false);
        clearCart();
        setStep('success');
      }, 1500);
    } catch (error) {
      registerCheckoutAttempt(false);
      setSecurityMessage(t('checkout.errors.generic'));
      setLoading(false);
    }
  };

  if (items.length === 0 && step !== 'success') {
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center gap-4">
        <span className="text-6xl">🛒</span>
        <h2 className="text-2xl font-bold text-gray-700">{t('checkout.empty')}</h2>
        <Link to="/products"><Button>{t('cart.browse')}</Button></Link>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center gap-6 px-4">
        <div className="bg-white rounded-3xl shadow-sm border border-amber-100 p-12 max-w-md w-full text-center">
          <div className="text-7xl mb-6">🎉</div>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">✓</span>
          </div>
          <h2 className="text-2xl font-bold font-serif text-gray-800 mb-3">{t('checkout.orderConfirmed')}</h2>
          <p className="text-gray-600 mb-2">{t('checkout.thanks')}</p>
          <p className="text-sm text-gray-500 mb-8">
            {t('checkout.confirmationEmail')}
          </p>
          <div className="bg-amber-50 rounded-xl p-4 mb-8">
            <p className="text-sm text-amber-800">
              {t('checkout.orderTotal')}: <span className="font-bold">${total.toFixed(2)}</span>
            </p>
            {lastMaskedCard && <p className="text-xs text-amber-700 mt-2">{lastMaskedCard}</p>}
          </div>
          <Link to="/">
            <Button className="w-full">{t('checkout.returnHome')}</Button>
          </Link>
          <Link to="/products">
            <button className="w-full mt-3 text-sm text-amber-700 hover:text-amber-900 transition-colors py-2">
              {t('cart.continueShopping')}
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold font-serif text-amber-900 mb-8">{t('checkout.title')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={handleChange}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {securityMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                  {securityMessage}
                </div>
              )}
              {checkoutDisabled && (
                <div className="bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-3 text-sm">
                  {t('checkout.orderDisabled')}
                </div>
              )}

              {/* Shipping Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6">
                <h2 className="text-lg font-bold text-amber-900 mb-4 font-serif">{t('checkout.shippingInfo')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label={t('checkout.firstName')}
                    id="firstName"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.firstName')}
                    error={errors.firstName}
                  />
                  <InputField
                    label={t('checkout.lastName')}
                    id="lastName"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.lastName')}
                    error={errors.lastName}
                  />
                  <InputField
                    label={t('checkout.email')}
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.email')}
                    error={errors.email}
                    className="sm:col-span-2"
                  />
                  <InputField
                    label={t('checkout.address')}
                    id="address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.address')}
                    error={errors.address}
                    className="sm:col-span-2"
                  />
                  <InputField
                    label={t('checkout.city')}
                    id="city"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.city')}
                    error={errors.city}
                  />
                  <InputField
                    label={t('checkout.zipCode')}
                    id="zipCode"
                    name="zipCode"
                    value={form.zipCode}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.zipCode')}
                    error={errors.zipCode}
                  />
                  <InputField
                    label={t('checkout.country')}
                    id="country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.country')}
                    error={errors.country}
                    className="sm:col-span-2"
                  />
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6">
                <h2 className="text-lg font-bold text-amber-900 mb-4 font-serif">{t('checkout.paymentInfo')}</h2>
                <div className="bg-amber-50 rounded-lg p-3 mb-4 text-sm text-amber-700 flex items-center gap-2">
                  <span>🔒</span>
                  <span>{t('checkout.simulatedPayment')}</span>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 mb-4 text-sm text-emerald-800">
                  {t('checkout.securityNotice')}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InputField
                    label={t('checkout.cardNumber')}
                    id="cardNumber"
                    name="cardNumber"
                    value={form.cardNumber}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.cardNumber')}
                    error={errors.cardNumber}
                    className="sm:col-span-2"
                    maxLength={19}
                    disabled={checkoutDisabled}
                  />
                  <InputField
                    label={t('checkout.cardName')}
                    id="cardName"
                    name="cardName"
                    value={form.cardName}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.cardName')}
                    error={errors.cardName}
                    className="sm:col-span-2"
                    disabled={checkoutDisabled}
                  />
                  <InputField
                    label={t('checkout.expiry')}
                    id="expiry"
                    name="expiry"
                    value={form.expiry}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.expiry')}
                    error={errors.expiry}
                    disabled={checkoutDisabled}
                  />
                  <InputField
                    label={t('checkout.cvv')}
                    id="cvv"
                    name="cvv"
                    value={form.cvv}
                    onChange={handleChange}
                    placeholder={t('checkout.placeholders.cvv')}
                    error={errors.cvv}
                    maxLength={4}
                    disabled={checkoutDisabled}
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full py-4 text-lg"
                disabled={loading || checkoutDisabled}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⏳</span>
                    {t('checkout.processing')}
                  </span>
                ) : checkoutDisabled ? (
                  t('checkout.orderDisabled')
                ) : (
                  `${t('checkout.completeOrder')} — $${total.toFixed(2)}`
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6 sticky top-24">
              <h2 className="text-lg font-bold font-serif text-amber-900 mb-4">{t('checkout.orderSummary')}</h2>
              <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
                {items.map(item => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-amber-50 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={item.images[0]} alt={item.name} className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">{t('cart.qty')}: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-semibold text-amber-800">${(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-amber-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.subtotal')}</span>
                  <span>${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.shipping')}</span>
                  <span>{shipping === 0 ? t('cart.free') : `$${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{t('cart.tax')}</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-amber-800 pt-2 border-t border-amber-100">
                  <span>{t('cart.total')}</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
